import React, { useState, useEffect } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { CheckCircleIcon, XCircleIcon, PaperClipIcon } from "@heroicons/react/24/outline";
import Pagination from "../../../components/common/Pagination";
import LoadingSpinner from "../../../components/common/LoadingSpinner";
import { withdrawalService } from "../services/withdrawalService";

const fmt = (n) => new Intl.NumberFormat("fr-FR").format(Number(n ?? 0));
const fcfa = (n) => `${fmt(Math.round(Number(n ?? 0)))} F`;
const dateTime = (d) =>
  d
    ? new Date(d).toLocaleString("fr-FR", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      })
    : "–";

const statusMeta = {
  pending: { t: "En attente", c: "bg-amber-50 text-amber-700" },
  approved: { t: "Effectué", c: "bg-green-50 text-green-700" },
  rejected: { t: "Refusé", c: "bg-red-50 text-red-700" },
};

const tabs = [
  { label: "Toutes", value: "all" },
  { label: "En attente", value: "pending" },
  { label: "Effectuées", value: "approved" },
  { label: "Refusées", value: "rejected" },
];

const WithdrawalList = () => {
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState("all");
  const [approveTarget, setApproveTarget] = useState(null);
  const [rejectTarget, setRejectTarget] = useState(null);
  const queryClient = useQueryClient();

  const { data, isLoading, isError } = useQuery({
    queryKey: ["withdrawals", page, statusFilter],
    queryFn: () =>
      withdrawalService.getWithdrawals({
        page,
        status: statusFilter !== "all" ? statusFilter : null,
      }),
  });

  useEffect(() => {
    setPage(1);
  }, [statusFilter]);

  const refresh = () => queryClient.invalidateQueries(["withdrawals"]);

  const approveMutation = useMutation({
    mutationFn: ({ id, proofFile }) => withdrawalService.approve(id, proofFile),
    onSuccess: () => {
      refresh();
      setApproveTarget(null);
    },
  });

  const rejectMutation = useMutation({
    mutationFn: ({ id, reason }) => withdrawalService.reject(id, reason),
    onSuccess: () => {
      refresh();
      setRejectTarget(null);
    },
  });

  if (isLoading) return <LoadingSpinner />;
  if (isError)
    return (
      <div className="p-4 text-sm text-red-500">
        Erreur lors du chargement des retraits.
      </div>
    );

  const withdrawals = data?.data || [];
  const pagination = data?.meta || {};

  return (
    <div className="bg-white min-h-full shadow-md rounded-lg">
      <div className="p-4 border-b border-gray-200">
        <h2 className="text-xl font-semibold text-gray-800">Retraits</h2>
        <p className="text-sm text-gray-500 mt-0.5">
          Demandes de retrait du solde des boutiques.
        </p>
      </div>

      <div className="border-b border-gray-200">
        <nav className="flex flex-wrap gap-2 px-4 py-3" aria-label="Tabs">
          {tabs.map((tab) => (
            <button
              key={tab.value}
              onClick={() => setStatusFilter(tab.value)}
              className={`px-4 py-2 text-sm font-medium rounded-md transition cursor-pointer ${
                statusFilter === tab.value
                  ? "bg-primary text-white shadow"
                  : "bg-gray-100 text-gray-600 hover:bg-gray-200"
              }`}
            >
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-primary text-white">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium uppercase">Boutique</th>
              <th className="px-4 py-3 text-left text-xs font-medium uppercase">Destinataire</th>
              <th className="px-4 py-3 text-left text-xs font-medium uppercase">Montant demandé</th>
              <th className="px-4 py-3 text-left text-xs font-medium uppercase">Numéro</th>
              <th className="px-4 py-3 text-left text-xs font-medium uppercase">Statut</th>
              <th className="px-4 py-3 text-left text-xs font-medium uppercase">Date</th>
              <th className="px-4 py-3 text-right text-xs font-medium uppercase">Actions</th>
            </tr>
          </thead>
          {withdrawals.length > 0 ? (
            <tbody className="bg-white divide-y divide-gray-200">
              {withdrawals.map((w) => {
                const sm = statusMeta[w.status] || statusMeta.pending;
                return (
                  <tr key={w.id}>
                    <td className="px-4 py-3 text-sm font-medium text-gray-800">
                      {w.shop_name || "–"}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-700">{w.recipient_name}</td>
                    <td className="px-4 py-3 text-sm text-gray-800">{fcfa(w.amount)}</td>
                    <td className="px-4 py-3 text-sm text-gray-600">{w.recipient_phone}</td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${sm.c}`}>
                        {sm.t}
                      </span>
                      {w.status !== "pending" && w.admin_note && (
                        <div className="text-xs text-gray-400 mt-1 max-w-[180px] truncate" title={w.admin_note}>
                          {w.admin_note}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-500">{dateTime(w.created_at)}</td>
                    <td className="px-4 py-3 text-right whitespace-nowrap">
                      {w.status === "pending" ? (
                        <>
                          <button
                            title="Valider avec preuve"
                            className="p-2 hover:bg-green-100 rounded cursor-pointer"
                            onClick={() => setApproveTarget(w)}
                          >
                            <CheckCircleIcon className="h-5 w-5 text-green-600" />
                          </button>
                          <button
                            title="Refuser"
                            className="p-2 hover:bg-red-100 rounded cursor-pointer"
                            onClick={() => setRejectTarget(w)}
                          >
                            <XCircleIcon className="h-5 w-5 text-red-500" />
                          </button>
                        </>
                      ) : w.proof_url ? (
                        <a
                          href={w.proof_url}
                          target="_blank"
                          rel="noreferrer"
                          title="Voir la preuve"
                          className="p-2 inline-flex hover:bg-gray-100 rounded cursor-pointer"
                        >
                          <PaperClipIcon className="h-5 w-5 text-gray-500" />
                        </a>
                      ) : (
                        <span className="text-gray-300 text-xs">–</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          ) : (
            <tbody>
              <tr>
                <td colSpan="7">
                  <div className="flex justify-center items-center py-10 text-gray-500 text-center text-sm">
                    Aucune demande de retrait pour le moment.
                  </div>
                </td>
              </tr>
            </tbody>
          )}
        </table>
      </div>

      <Pagination
        currentPage={page}
        totalItems={pagination.total}
        itemsPerPage={pagination.per_page}
        onPageChange={setPage}
      />

      {approveTarget && (
        <ApproveModal
          withdrawal={approveTarget}
          isLoading={approveMutation.isPending}
          onClose={() => setApproveTarget(null)}
          onConfirm={(proofFile) =>
            approveMutation.mutate({ id: approveTarget.id, proofFile })
          }
        />
      )}

      {rejectTarget && (
        <RejectModal
          withdrawal={rejectTarget}
          isLoading={rejectMutation.isPending}
          onClose={() => setRejectTarget(null)}
          onConfirm={(reason) =>
            rejectMutation.mutate({ id: rejectTarget.id, reason })
          }
        />
      )}
    </div>
  );
};

const ApproveModal = ({ withdrawal, isLoading, onClose, onConfirm }) => {
  const [file, setFile] = useState(null);

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-lg max-w-md w-full p-5">
        <h3 className="text-lg font-semibold text-gray-800">Valider le retrait</h3>
        <p className="text-sm text-gray-500 mt-1">
          {fcfa(withdrawal.amount)} pour « {withdrawal.shop_name} » — confirmez que le paiement
          a été effectué hors plateforme, puis joignez la preuve (capture, reçu...).
        </p>

        <label className="block mt-4 text-sm font-medium text-gray-700">
          Preuve de paiement
        </label>
        <input
          type="file"
          accept="image/*"
          onChange={(e) => setFile(e.target.files?.[0] || null)}
          className="mt-1 block w-full text-sm text-gray-600"
        />

        <div className="flex justify-end gap-2 mt-5">
          <button
            onClick={onClose}
            disabled={isLoading}
            className="px-4 py-2 text-sm rounded-md text-gray-600 hover:bg-gray-100 cursor-pointer"
          >
            Annuler
          </button>
          <button
            onClick={() => file && onConfirm(file)}
            disabled={!file || isLoading}
            className="px-4 py-2 text-sm rounded-md bg-green-600 text-white hover:bg-green-700 disabled:opacity-50 cursor-pointer"
          >
            {isLoading ? "Validation..." : "Valider"}
          </button>
        </div>
      </div>
    </div>
  );
};

const RejectModal = ({ withdrawal, isLoading, onClose, onConfirm }) => {
  const [reason, setReason] = useState("");

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-lg max-w-md w-full p-5">
        <h3 className="text-lg font-semibold text-gray-800">Refuser le retrait</h3>
        <p className="text-sm text-gray-500 mt-1">
          {fcfa(withdrawal.amount)} pour « {withdrawal.shop_name} » — le montant sera recrédité
          sur le solde de la boutique.
        </p>

        <label className="block mt-4 text-sm font-medium text-gray-700">Motif du refus</label>
        <textarea
          rows={3}
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="Ex. numéro invalide, coordonnées incorrectes..."
          className="mt-1 block w-full border border-gray-300 rounded-md p-2 text-sm"
        />

        <div className="flex justify-end gap-2 mt-5">
          <button
            onClick={onClose}
            disabled={isLoading}
            className="px-4 py-2 text-sm rounded-md text-gray-600 hover:bg-gray-100 cursor-pointer"
          >
            Annuler
          </button>
          <button
            onClick={() => reason.trim() && onConfirm(reason.trim())}
            disabled={!reason.trim() || isLoading}
            className="px-4 py-2 text-sm rounded-md bg-red-500 text-white hover:bg-red-600 disabled:opacity-50 cursor-pointer"
          >
            {isLoading ? "Envoi..." : "Refuser"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default WithdrawalList;
